import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  ShieldAlert, Loader2, ChevronDown, ChevronUp, Eye, AlertTriangle,
  Ship, FileText, PoundSterling, Calendar
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBreakGlass = () => {
  const [expanded, setExpanded] = useState(false);
  const [userId, setUserId] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  const handleAccess = async () => {
    if (!userId.trim() || !reason.trim()) {
      toast.error('User ID and reason are required');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/break-glass`, {
        user_id: userId.trim(),
        reason: reason.trim()
      });
      setResult(res.data);
      toast.success('Emergency access granted — action logged');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Access denied');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

  return (
    <Card className="bg-slate-800 border-red-500/20">
      <CardHeader className="pb-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-red-400" />
            GDPR Break-Glass Access
          </CardTitle>
          {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="pt-0 space-y-4">
          {!result ? (
            <>
              <div className="p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
                  <div className="text-xs text-slate-400">
                    <p className="text-red-300 font-medium mb-1">Emergency Access Only</p>
                    <p>This grants temporary access to a user's full personal data for debugging purposes. 
                    All access is recorded in the audit log with your justification, in compliance with GDPR Article 32.</p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="space-y-1">
                  <label className="text-xs text-slate-400">User ID</label>
                  <Input value={userId} onChange={(e) => setUserId(e.target.value)}
                    placeholder="Paste the user's UUID" className="bg-slate-900 border-slate-600 text-white h-9 text-sm font-mono"
                    data-testid="break-glass-user-id" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-slate-400">Reason for access (recorded in audit log)</label>
                  <Input value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Investigating SED calculation discrepancy reported by user"
                    className="bg-slate-900 border-slate-600 text-white h-9 text-sm"
                    data-testid="break-glass-reason" />
                </div>
                {!confirmed ? (
                  <Button onClick={() => setConfirmed(true)} disabled={!userId.trim() || !reason.trim()}
                    className="bg-red-600/80 hover:bg-red-600 text-white h-9 text-sm" data-testid="break-glass-confirm-step">
                    <Eye className="h-4 w-4 mr-2" />Request Access
                  </Button>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button onClick={handleAccess} disabled={loading}
                      className="bg-red-600 hover:bg-red-700 text-white h-9 text-sm" data-testid="break-glass-submit">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldAlert className="h-4 w-4 mr-2" />}
                      Confirm — Access User Data
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setConfirmed(false)} className="text-slate-400 h-9">Cancel</Button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <div className="p-3 bg-red-500/5 border border-red-500/20 rounded-lg text-xs text-red-300">
                Access granted at {formatDate(result.access_granted_at)}. {result.audit_notice}
              </div>

              {/* User Info */}
              <div className="p-3 bg-slate-700/30 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">User Profile</p>
                <p className="text-sm text-white">{result.user?.name}</p>
                <p className="text-xs text-slate-400">{result.user?.email}</p>
                <p className="text-xs text-slate-500 mt-1">Role: {result.user?.role} | Joined: {formatDate(result.user?.created_at)}</p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-2">
                <div className="p-2 bg-slate-700/30 rounded text-center">
                  <p className="text-xs text-slate-500">Qualifying Days</p>
                  <p className="text-lg font-bold text-white">{result.stats?.qualifying_days || 0}</p>
                </div>
                <div className="p-2 bg-slate-700/30 rounded text-center">
                  <p className="text-xs text-slate-500">Trips</p>
                  <p className="text-lg font-bold text-white">{result.trips?.length || 0}</p>
                </div>
                <div className="p-2 bg-slate-700/30 rounded text-center">
                  <p className="text-xs text-slate-500">Documents</p>
                  <p className="text-lg font-bold text-white">{result.documents?.length || 0}</p>
                </div>
              </div>

              {/* Recent Trips */}
              {result.trips?.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 font-medium mb-2">Recent Trips (up to 50)</p>
                  <div className="space-y-1 max-h-[200px] overflow-y-auto">
                    {result.trips.slice(0, 10).map(trip => (
                      <div key={trip.id} className="flex items-center justify-between p-2 bg-slate-700/20 rounded text-xs">
                        <div className="flex items-center gap-2">
                          <Ship className="h-3 w-3 text-slate-500" />
                          <span className="text-white">{trip.trip_name || trip.vessel_name || 'Trip'}</span>
                          <span className="text-slate-500">{formatDate(trip.date_left_uk)} — {formatDate(trip.date_returned_uk)}</span>
                        </div>
                        <span className="text-amber-400">£{(trip.total_earnings || 0).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Button variant="outline" size="sm" onClick={() => { setResult(null); setConfirmed(false); setUserId(''); setReason(''); }}
                className="border-slate-600 text-slate-300 h-8 text-xs" data-testid="break-glass-close">
                Close & Clear
              </Button>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default AdminBreakGlass;
